import {
  ArrowRight,
  BadgeCheck,
  FileSpreadsheet,
  FileText,
  Globe2,
  LockKeyhole,
} from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router";

import { PdfSampleCarousel } from "@/components/invoice/PdfSampleCarousel";
import { PublicFooter } from "@/components/layout/PublicFooter";
import { Seo } from "@/lib/seo";
import seoLandingPages from "./seo-landing-pages.json";

export interface SeoLandingPageConfig {
  path: string;
  title: string;
  metaDescription: string;
  h1: string;
  lead: string;
  primaryCta: string;
  primaryTo: string;
  secondaryCta?: string;
  secondaryTo?: string;
  sampleCountry?: string;
  sampleTitle: string;
  sampleSubtitle: string;
  intentLabel: string;
  featureIntro: string;
  features: string[];
  quickStarts?: Array<{ label: string; to: string; body: string }>;
  paidNote: string;
  faqs: Array<{ question: string; answer: string }>;
}

export const SEO_LANDING_PAGES: SeoLandingPageConfig[] = seoLandingPages;

export const SEO_LANDING_PATHS = SEO_LANDING_PAGES.map((page) => page.path);

export function seoLandingCanonicalPath(page: SeoLandingPageConfig) {
  return page.path.endsWith("/") ? page.path : `${page.path}/`;
}

export function SeoLandingPage({ page }: { page: SeoLandingPageConfig }) {
  const canonicalPath = seoLandingCanonicalPath(page);
  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: page.h1,
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      url: `https://invoice.polenest.com${canonicalPath}`,
      description: page.metaDescription,
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "USD",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: page.faqs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer,
        },
      })),
    },
  ];

  return (
    <div className="flex min-h-dvh flex-col bg-canvas text-primary">
      <Seo
        title={page.title}
        description={page.metaDescription}
        path={canonicalPath}
        schema={schema}
      />

      <header className="sticky top-0 z-50 border-b border-border bg-canvas/90 backdrop-blur supports-[backdrop-filter]:bg-canvas/70">
        <div className="mx-auto flex h-14 max-w-6xl items-center justify-between px-4">
          <Link to="/app" className="font-semibold tracking-tight">
            Invoice Builder
          </Link>
          <nav className="flex items-center gap-2 text-sm">
            <Link to="/app" className="rounded-md px-3 py-1.5 text-secondary hover:bg-elevated">
              Open app
            </Link>
            <Link
              to="/free-invoice-generator/"
              className="rounded-md border border-border bg-surface px-3 py-1.5 text-secondary transition-colors hover:bg-elevated hover:text-primary"
            >
              Free invoice
            </Link>
          </nav>
        </div>
      </header>

      <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-8">
        <section className="grid min-w-0 gap-6 lg:grid-cols-[minmax(0,1fr)_420px] lg:items-start">
          <div className="min-w-0 space-y-5">
            <div>
              <p className="text-sm font-medium text-accent">{page.intentLabel}</p>
              <h1 className="mt-2 max-w-3xl break-words text-3xl font-semibold tracking-tight sm:text-4xl">
                {page.h1}
              </h1>
              <p className="mt-4 max-w-2xl text-base leading-7 text-secondary">{page.lead}</p>
            </div>

            <div className="flex flex-wrap gap-2">
              <Link
                to={page.primaryTo}
                className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-accent px-4 text-sm font-medium text-accent-fg transition-colors hover:bg-accent-hover"
              >
                {page.primaryCta}
                <ArrowRight size={16} />
              </Link>
              {page.secondaryCta && page.secondaryTo && (
                <Link
                  to={page.secondaryTo}
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-border bg-transparent px-4 text-sm font-medium text-primary transition-colors hover:bg-elevated"
                >
                  {page.secondaryCta}
                </Link>
              )}
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <TrustItem
                icon={<LockKeyhole size={18} />}
                title="Browser-local"
                body="Invoice contents, seller details, customer details, and uploaded spreadsheets are not stored on a server."
              />
              <TrustItem
                icon={<FileText size={18} />}
                title="Manual free"
                body="Create unlimited one-at-a-time invoices, estimates, and credit notes."
              />
              <TrustItem
                icon={<FileSpreadsheet size={18} />}
                title="Bulk paid"
                body="Spreadsheet mapping and batch PDF/XML/ZIP export are premium tools."
              />
            </div>
          </div>

          <PdfSampleCarousel
            className="min-w-0 lg:sticky lg:top-20"
            title={page.sampleTitle}
            subtitle={page.sampleSubtitle}
            selectedCountry={page.sampleCountry}
            limit={4}
            density="prominent"
          />
        </section>

        <section className="mt-8 grid min-w-0 gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="min-w-0 rounded-lg border border-border bg-surface p-5">
            <div className="flex items-center gap-2">
              <BadgeCheck size={18} className="text-success" />
              <h2 className="text-lg font-semibold">What this invoice tool supports</h2>
            </div>
            <p className="mt-2 text-sm leading-6 text-secondary">{page.featureIntro}</p>
            <ul className="mt-4 grid gap-3 text-sm text-secondary md:grid-cols-2">
              {page.features.map((feature) => (
                <li key={feature} className="flex gap-2">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          <aside className="min-w-0 rounded-lg border border-border bg-surface p-5">
            <div className="flex items-center gap-2">
              <Globe2 size={18} className="text-secondary" />
              <h2 className="text-base font-semibold">Free and premium boundary</h2>
            </div>
            <p className="mt-3 text-sm leading-6 text-secondary">{page.paidNote}</p>
            <Link
              to="/app/bulk"
              className="mt-4 inline-flex h-10 w-full items-center justify-center gap-2 rounded-md border border-border bg-transparent px-4 text-sm font-medium text-primary transition-colors hover:bg-elevated"
            >
              Preview bulk import
            </Link>
          </aside>
        </section>

        {page.quickStarts?.length ? (
          <section className="mt-8 rounded-lg border border-border bg-surface p-5">
            <h2 className="text-lg font-semibold">Seller-country quick starts</h2>
            <p className="mt-2 text-sm leading-6 text-secondary">
              Start from the seller country. The seller country controls the compatible PDF layout
              and structured output path.
            </p>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              {page.quickStarts.map((quickStart) => (
                <Link
                  key={quickStart.to}
                  to={quickStart.to}
                  className="rounded-md border border-border bg-elevated p-4 text-left transition-colors hover:border-accent/70 hover:bg-surface"
                >
                  <span className="text-sm font-semibold text-primary">{quickStart.label}</span>
                  <span className="mt-2 block text-sm leading-6 text-secondary">
                    {quickStart.body}
                  </span>
                </Link>
              ))}
            </div>
          </section>
        ) : null}

        <section className="mt-8 rounded-lg border border-border bg-surface p-5">
          <h2 className="text-lg font-semibold">Questions about {page.h1.toLowerCase()}</h2>
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            {page.faqs.map((faq) => (
              <article
                key={faq.question}
                className="rounded-md border border-border bg-elevated p-4"
              >
                <h3 className="text-sm font-semibold">{faq.question}</h3>
                <p className="mt-2 text-sm leading-6 text-secondary">{faq.answer}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="mt-8 rounded-lg border border-border bg-surface p-5">
          <h2 className="text-lg font-semibold">Popular invoice generator searches</h2>
          <div className="mt-3 flex flex-wrap gap-2 text-sm">
            {SEO_LANDING_PAGES.map((linkPage) => (
              <Link
                key={linkPage.path}
                to={seoLandingCanonicalPath(linkPage)}
                className="rounded-md border border-border bg-elevated px-3 py-2 text-secondary transition-colors hover:border-accent/70 hover:text-primary"
              >
                {linkPage.h1}
              </Link>
            ))}
          </div>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}

function TrustItem({ icon, title, body }: { icon: ReactNode; title: string; body: string }) {
  return (
    <div className="rounded-lg border border-border bg-surface p-4">
      <div className="flex items-center gap-2 text-sm font-semibold">
        <span className="text-accent">{icon}</span>
        <span>{title}</span>
      </div>
      <p className="mt-2 text-xs leading-5 text-secondary">{body}</p>
    </div>
  );
}
